import React, { useEffect, useRef, useState } from 'react';
import { menuItems } from '../data/mock';

const Menu = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'classic', label: 'Classic' },
    { id: 'gourmet', label: 'Gourmet' },
    { id: 'fusion', label: 'Fusion' },
  ];

  const filteredItems =
    activeFilter === 'all'
      ? menuItems
      : menuItems.filter((item) => item.category === activeFilter);

  return (
    <section
      id="menu"
      ref={sectionRef}
      className="relative py-24 lg:py-32 bg-white overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-1 italian-stripe" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#c9a962]/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="text-[#c9a962] text-sm font-semibold tracking-[0.2em] uppercase">
            Our Menu
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e1919] mt-4 mb-6">
            Discover Our
            <span className="block gradient-text">Signature Creations</span>
          </h2>
          <p className="text-lg text-[#736c64] leading-relaxed">
            Each CRISPYzza is a masterpiece crafted with premium ingredients and
            Italian passion. All items are certified Halal.
          </p>
        </div>

        {/* Filter Tabs */}
        <div
          className={`flex justify-center gap-2 mb-12 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeFilter === filter.id
                  ? 'bg-[#c9a962] text-[#1e1919] shadow-lg shadow-[#c9a962]/20'
                  : 'bg-[#f7f5f2] text-[#736c64] hover:bg-[#e8e3db]'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Menu Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredItems.map((item, index) => (
            <div
              key={item.id}
              className={`group bg-[#f7f5f2] rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e1919]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Tags */}
                <div className="absolute top-4 left-4 flex gap-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-semibold text-[#1e1919] rounded-full capitalize"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-display text-2xl font-bold text-[#1e1919] mb-2 group-hover:text-[#c9a962] transition-colors">
                  {item.name}
                </h3>
                <p className="text-[#736c64] leading-relaxed">
                  {item.description}
                </p>

                <div className="mt-6 flex items-center justify-between">
                  <span className="text-sm font-medium text-[#c9a962] uppercase tracking-wider">
                    {item.category}
                  </span>
                  <button className="px-4 py-2 bg-[#1e1919] text-white text-sm font-semibold rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-[#c9a962] hover:text-[#1e1919]">
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All CTA */}
        <div
          className={`text-center mt-16 transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <a
            href="#contact"
            className="inline-flex items-center gap-3 bg-[#1e1919] text-white px-8 py-4 text-base font-semibold rounded transition-all duration-300 hover:bg-[#c9a962] hover:text-[#1e1919] hover:shadow-xl"
          >
            Order Now
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Menu;
